import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { post } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { Alert, Button, Card, CardContent, CardHeader, Field, PasswordInput } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';

/**
 * Change your own password.
 *
 * Two ways in. Forced: signed in on a temporary password (an owner reset, a
 * first sign-in) — the token reaches this screen and nothing else, so it is
 * the only thing the user can do until they finish here. Voluntary: from the
 * user menu at any time. Either way the change ends every session, including
 * this one, so the page signs out and returns to the login screen.
 */
export function ChangePasswordPage() {
  const { user, loading, mustChangePassword, signOut } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!loading && !user) {
    navigate('/login', { replace: true });
    return null;
  }

  const mismatch = confirm.length > 0 && newPassword !== confirm;
  // Mirrors changePasswordSchema, so what turns green here is what the server
  // accepts — the list is the rule, not a paraphrase of it.
  const rules = [
    { label: 'At least 10 characters', ok: newPassword.length >= 10 },
    { label: 'An uppercase letter (A–Z)', ok: /[A-Z]/.test(newPassword) },
    { label: 'A lowercase letter (a–z)', ok: /[a-z]/.test(newPassword) },
    { label: 'A digit (0–9)', ok: /\d/.test(newPassword) },
    { label: 'A symbol (! @ # $ % …)', ok: /[^A-Za-z0-9]/.test(newPassword) },
  ];
  const strong = rules.every((rule) => rule.ok);
  const ready = currentPassword.length >= 8 && strong && newPassword === confirm;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await post('/auth/change-password', { currentPassword, newPassword, confirmPassword: confirm });
      // Every session was just ended, this one included: a fresh sign-in is
      // the honest next step, and the login screen says why.
      await signOut();
      toast.success('Password changed', 'Sign in again with the new one.');
      navigate('/login', { replace: true });
    } catch (caught) {
      setError(describeError(caught));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <h1 className="text-lg font-semibold leading-none tracking-tight">
            {mustChangePassword ? 'Set a new password' : 'Change your password'}
          </h1>
          {mustChangePassword && (
            <p className="mt-1 text-sm text-muted-foreground">
              You signed in with a temporary password. Choose your own before continuing — nothing
              else is available until you do.
            </p>
          )}
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            {error && <Alert variant="destructive">{error}</Alert>}

            <Field label={mustChangePassword ? 'Temporary password' : 'Current password'} required>
              <PasswordInput
                autoComplete="current-password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                autoFocus
              />
            </Field>

            <Field label="New password" required>
              <PasswordInput
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </Field>
            <ul className="-mt-2 space-y-1 text-xs" aria-live="polite">
              {rules.map((rule) => (
                <li
                  key={rule.label}
                  className={rule.ok ? 'flex items-center gap-1.5 text-success' : 'flex items-center gap-1.5 text-muted-foreground'}
                >
                  <span aria-hidden className="inline-block w-4 text-center font-semibold">{rule.ok ? '✓' : '○'}</span>
                  {rule.label}
                </li>
              ))}
            </ul>

            <Field label="Confirm new password" required error={mismatch ? 'The two passwords differ' : undefined}>
              <PasswordInput
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
              />
            </Field>

            <div className="flex items-center justify-between gap-2 pt-2">
              {mustChangePassword ? (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={async () => {
                    await signOut();
                    navigate('/login', { replace: true });
                  }}
                >
                  Sign out
                </Button>
              ) : (
                <Button type="button" variant="ghost" onClick={() => navigate(-1)}>
                  Back
                </Button>
              )}
              <Button type="submit" loading={busy} disabled={!ready}>
                Change password
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
